import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import {
  CopilotWorkflow,
  useCopilotStore,
} from "../../store/copilot/CopilotStore";
import { Header } from "./Header";
import { MessageInput } from "./MessageInput";
import { createThread, sendMessage } from "../../apis/copilot/copilot";
import { Messages } from "./Messages";
import { getLoggedUserProfile } from "../../utils/auth";
import { useCopilotContext } from "../../hooks/useCopilotContext";
import { CopilotContext, Message, MessageType } from "../../apis/copilot/types";
import { mergeMessages } from "./utils";
import { useLocation } from "react-router-dom";
import { useFeatureFlag } from "../../hooks/useLaunchDarkly";

const PROJECT_SEGMENT_IMPROVEMENTS_FF = "maProjectSegmentImprovements";

const buildInitialUserMessageContent = (
  workflow: CopilotWorkflow,
  isProjectSegmentImprovementsEnabled: boolean,
  userEmail: string | undefined
): string => {
  const fallback = `I need help defining my ${workflow.length === 3 ? workflow.toUpperCase() : "Competitors"}`;

  if (
    !isProjectSegmentImprovementsEnabled ||
    workflow !== CopilotWorkflow.TAM
  ) {
    return fallback;
  }

  const domain = userEmail?.split("@")[1];
  if (!domain) return fallback;

  return `I need help defining the TAM for ${domain}`;
};

export const Copilot: React.FC = () => {
  const userProfile = getLoggedUserProfile();
  const userName = userProfile?.email?.split("@")[0];
  const location = useLocation();

  const {
    workflow,
    isVisible,
    threadId,
    hideCopilot,
    setThreadId,
    setMessages,
    setIsLoading,
    isMinimized,
    toggleMinimized,
  } = useCopilotStore();

  const context = useCopilotContext();
  const isProjectSegmentImprovementsEnabled = useFeatureFlag(
    PROJECT_SEGMENT_IMPROVEMENTS_FF
  );

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      const { id, messages } = await createThread(
        userName as string,
        workflow as CopilotWorkflow,
        context as CopilotContext
      );

      const initialContent = buildInitialUserMessageContent(
        workflow as CopilotWorkflow,
        isProjectSegmentImprovementsEnabled,
        userProfile?.email
      );

      setIsLoading(false);

      const initialUserMessage: Message = {
        id: Math.random().toString(),
        type: MessageType.HUMAN_MESSAGE,
        rate: null,
        payload: {
          content: initialContent,
          timestamp: new Date(),
        },
      };

      setMessages([...messages, initialUserMessage]);

      setIsLoading(true);

      sendMessage(
        id as string,
        initialUserMessage.payload.content,
        context as CopilotContext,
        (answers: Message[]) => {
          setMessages((currentMessages) =>
            mergeMessages(currentMessages, answers)
          );
        },
        () => {
          setIsLoading(false);
        }
      );

      setThreadId(id);
    };

    if (isVisible && workflow && !threadId && context) {
      init();
    }
  }, [
    isVisible,
    workflow,
    threadId,
    setIsLoading,
    setMessages,
    setThreadId,
    userName,
    context,
    isProjectSegmentImprovementsEnabled,
  ]);

  useEffect(() => {
    if (isVisible && !location.pathname.includes("/configuration"))
      hideCopilot();
  }, [isVisible, location, hideCopilot]);

  if (!isVisible) return null;

  return (
    <Box
      component={Paper}
      elevation={3}
      sx={{
        position: "fixed",
        right: "45px",
        bottom: "45px",
        width: 450,
        height: isMinimized ? 65 : "calc(100vh - 160px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        zIndex: 9999,
        overflow: "hidden",
        boxShadow: 10,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Header
        onClose={hideCopilot}
        isMinimized={isMinimized}
        onToggleMinimized={toggleMinimized}
      />
      <Messages />
      <MessageInput />
    </Box>
  );
};
